import { Fragment, useEffect, useRef } from "react";
import { Box, Button } from "@mui/material";
import strings from "global/constants/StringConstants";
import styles from "../AiWebForm.styles";
import { versionOrigin, versionWhen } from "../AiWebFormHelper";
import { AiVersionList } from "../AiWebFormTypesAndValidation";
import { CloseGlyph, GlobeGlyph, HistoryGlyph, InfoGlyph, RestoreGlyph } from "./AiWebFormIcons";

const copy = strings.aiWebForm;

interface Props {
  versions: AiVersionList | null;
  /** The version in the preview (selected), else the current one. */
  selected: number | null;
  /** Phone layout: a full-screen sheet below the tab bar. */
  sheet: boolean;
  canEdit: boolean;
  onSelect: (version: number) => void;
  onRestore: (version: number) => void;
  onPublish: (version: number) => void;
  onClose: () => void;
}

/** Newest first; the selected row offers Restore and Publish this version. */
export const AiVersionsPanel = ({ versions, selected, sheet, canEdit, onSelect, onRestore, onPublish, onClose }: Props) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const rows = [...(versions?.versions ?? [])].reverse();
  const current = versions?.currentVersion ?? null;

  return (
    <Box
      sx={sheet ? styles.versionsSheet : styles.versionsOverlay}
      role="dialog"
      aria-label={copy.versionsTitle}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      <Box sx={styles.versionsHeader}>
        <HistoryGlyph />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box component="h2" sx={styles.panelTitle}>
            {copy.versionsTitle}
          </Box>
          <Box sx={styles.panelSubtitle}>{copy.versionsSubtitle}</Box>
        </Box>
        <Box component="button" type="button" ref={closeRef} sx={styles.moreButton} aria-label={copy.closeVersions} onClick={onClose}>
          <CloseGlyph />
        </Box>
      </Box>
      <Box sx={styles.versionsList} role="list">
        {rows.map((version, index) => {
          const isSelected = version.version === selected && version.version !== current;
          const showActions = isSelected && canEdit;
          return (
            <Fragment key={version.version}>
              {index > 0 && <Box sx={styles.versionDivider} role="presentation" />}
              {/* The row's own button previews it; Restore / Publish sit beside it, never inside it. */}
              <Box role="listitem" sx={styles.versionRow(isSelected)}>
                <Box
                  component="button"
                  type="button"
                  aria-pressed={isSelected}
                  sx={styles.versionSelect}
                  onClick={() => onSelect(version.version)}
                >
                  <Box component="span" sx={styles.versionTile(isSelected)}>
                    v{version.version}
                  </Box>
                  <Box component="span" sx={styles.versionText}>
                    <Box component="span" sx={styles.versionTitle}>
                      <span>{versionOrigin(version)}</span>
                      {version.version === current && (
                        <Box component="span" sx={styles.currentBadge}>
                          {copy.current}
                        </Box>
                      )}
                      {version.version === versions?.publishedVersion && (
                        <Box component="span" sx={styles.publishedBadge}>
                          {copy.publishedBadge}
                        </Box>
                      )}
                    </Box>
                    <Box component="span" sx={styles.versionWhen}>
                      {versionWhen(version.createdAt)}
                      {version.byMe === false && version.origin !== "generated" ? ` · ${copy.byColleague}` : ""}
                    </Box>
                  </Box>
                </Box>
                {showActions && (
                  <Box sx={styles.versionActions}>
                    <Button
                      size="small"
                      variant="contained"
                      sx={styles.smallButton}
                      startIcon={<RestoreGlyph />}
                      onClick={() => onRestore(version.version)}
                    >
                      {copy.restore}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={styles.smallButton}
                      startIcon={<GlobeGlyph />}
                      onClick={() => onPublish(version.version)}
                    >
                      {copy.publishThisVersion}
                    </Button>
                  </Box>
                )}
              </Box>
            </Fragment>
          );
        })}
      </Box>
      <Box sx={styles.versionsFoot}>
        <Box sx={styles.infoNotice}>
          <InfoGlyph />
          <span>{copy.restoreNote}</span>
        </Box>
      </Box>
    </Box>
  );
};
